import { httpClient } from '@/core/api/http-client'
import type { CategorySales } from '@/domain/entities/category-sales.entity'
import type { KpiMetric } from '@/domain/entities/metric.entity'
import type { Order } from '@/domain/entities/order.entity'
import type { OutletSales } from '@/domain/entities/outlet-sales.entity'
import type { RevenuePoint } from '@/domain/entities/revenue.entity'
import type {
  DashboardFilter,
  DashboardOverview,
  DashboardRepository,
  DateRange,
} from '@/domain/repositories/dashboard.repository'
import { unwrap, unwrapList } from '@/infrastructure/shared/unwrap'
import type {
  CategorySalesDto,
  DashboardOverviewDto,
  KpiMetricDto,
  OrderDto,
  OutletSalesDto,
  RevenuePointDto,
} from './dashboard.dto'
import { dashboardMapper } from './dashboard.mapper'

/**
 * Adapter HTTP — implementasi DashboardRepository yang memanggil REST API.
 * Menggunakan helper `unwrap` agar response envelope API `{ success, data }`
 * diekstrak dengan sempurna sebelum di-map ke domain entity.
 */
export class DashboardHttpRepository implements DashboardRepository {
  async getOverview(filter: DashboardFilter): Promise<DashboardOverview> {
    const raw = await httpClient.get<unknown>('/dashboard/overview', {
      params: {
        period: filter.period,
        start_date: filter.startDate,
        end_date: filter.endDate,
        outlet_id: filter.outletId ?? undefined,
      },
    })
    const dto = unwrap<DashboardOverviewDto>(raw)
    return dashboardMapper.toOverview(dto)
  }

  async getMetrics(range: DateRange, filter?: DashboardFilter): Promise<KpiMetric[]> {
    const raw = await httpClient.get<unknown>('/dashboard/metrics', {
      params: {
        range,
        start_date: filter?.startDate,
        end_date: filter?.endDate,
        outlet_id: filter?.outletId ?? undefined,
      },
    })
    const dtos = unwrapList<KpiMetricDto>(raw)
    return dtos.map(dashboardMapper.toMetric)
  }

  async getRevenueSeries(range: DateRange, filter?: DashboardFilter): Promise<RevenuePoint[]> {
    const raw = await httpClient.get<unknown>('/dashboard/revenue', {
      params: {
        range,
        start_date: filter?.startDate,
        end_date: filter?.endDate,
        outlet_id: filter?.outletId ?? undefined,
      },
    })
    const dtos = unwrapList<RevenuePointDto>(raw)
    return dtos.map(dashboardMapper.toRevenuePoint)
  }

  async getCategorySales(range: DateRange, filter?: DashboardFilter): Promise<CategorySales[]> {
    const raw = await httpClient.get<unknown>('/dashboard/category-sales', {
      params: {
        range,
        start_date: filter?.startDate,
        end_date: filter?.endDate,
        outlet_id: filter?.outletId ?? undefined,
      },
    })
    const dtos = unwrapList<CategorySalesDto>(raw)
    return dtos.map(dashboardMapper.toCategorySales)
  }

  async getSalesByOutlet(range: DateRange, filter?: DashboardFilter): Promise<OutletSales[]> {
    const raw = await httpClient.get<unknown>('/dashboard/outlet-sales', {
      params: {
        range,
        start_date: filter?.startDate,
        end_date: filter?.endDate,
        outlet_id: filter?.outletId ?? undefined,
      },
    })
    const dtos = unwrapList<OutletSalesDto>(raw)
    return dtos.map(dashboardMapper.toOutletSales)
  }

  async getRecentOrders(range: DateRange, filter?: DashboardFilter): Promise<Order[]> {
    const raw = await httpClient.get<unknown>('/dashboard/orders', {
      params: {
        range,
        start_date: filter?.startDate,
        end_date: filter?.endDate,
        outlet_id: filter?.outletId ?? undefined,
      },
    })
    const dtos = unwrapList<OrderDto>(raw)
    return dtos.map(dashboardMapper.toOrder)
  }
}
